import { ErrorInfo } from "react"
import { ScrollView, TextStyle, View, ViewStyle, Image, ImageStyle } from "react-native"

import { Button } from "@/components/Button"
import { Screen } from "@/components/Screen"
import { Text } from "@/components/Text"
import { useAppTheme } from "@/theme/context"
import type { ThemedStyle } from "@/theme/types"

export interface ErrorDetailsProps {
  error: Error
  errorInfo: ErrorInfo | null
  onReset(): void
}

/**
 * Renders the error details screen.
 * @param {ErrorDetailsProps} props - The props for the `ErrorDetails` component.
 * @returns {JSX.Element} The rendered `ErrorDetails` component.
 */
export function ErrorDetails(props: ErrorDetailsProps) {
  const { themed } = useAppTheme()
  return (
    <Screen
      preset="fixed"
      safeAreaEdges={["top", "bottom"]}
      contentContainerStyle={themed($contentContainer)}
    >
      <View style={$topSection}>
        <Image
          source={require("@assets/images/FleetHubLogo.png")}
          style={$logo}
          resizeMode="contain"
        />
        <Text style={themed($heading)} preset="subheading" tx="errorScreen:title" />
        <Text style={themed($subtitle)} tx="errorScreen:friendlySubtitle" />
        <Text style={themed($contactSupport)} tx="errorScreen:contactSupport" />
      </View>

      <ScrollView
        style={themed($errorSection)}
        contentContainerStyle={themed($errorSectionContentContainer)}
      >
        <Text style={themed($errorContent)} weight="bold" text={`${props.error}`.trim()} />
        <Text
          selectable
          style={themed($errorBacktrace)}
          text={`${props.errorInfo?.componentStack ?? ""}`.trim()}
        />
      </ScrollView>

      <Button
        style={themed($resetButton)}
        textStyle={themed($resetButtonText)}
        onPress={props.onReset}
        tx="errorScreen:reset"
      />
    </Screen>
  )
}

const $contentContainer: ThemedStyle<ViewStyle> = ({ spacing, colors }) => ({
  alignItems: "center",
  paddingHorizontal: spacing.lg,
  paddingTop: spacing.xl,
  flex: 1,
  backgroundColor: colors.background,
})

const $topSection: ViewStyle = {
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
}

const $logo: ImageStyle = {
  width: 200,
  height: 80,
  marginBottom: 32,
}

const $heading: ThemedStyle<TextStyle> = ({ colors, spacing }) => ({
  color: colors.text,
  marginBottom: spacing.sm,
  textAlign: "center",
})

const $subtitle: ThemedStyle<TextStyle> = ({ colors, spacing }) => ({
  color: colors.textDim,
  marginBottom: spacing.md,
  textAlign: "center",
})

const $contactSupport: ThemedStyle<TextStyle> = ({ colors, spacing }) => ({
  color: colors.primary,
  marginTop: spacing.xs,
  textAlign: "center",
  fontSize: 16,
  fontWeight: "600",
})

const $errorSection: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  flex: 2,
  backgroundColor: colors.palette.neutral200,
  marginVertical: spacing.md,
  borderRadius: 12,
  borderWidth: 1,
  borderColor: colors.border,
})

const $errorSectionContentContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  padding: spacing.md,
})

const $errorContent: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.error,
})

const $errorBacktrace: ThemedStyle<TextStyle> = ({ colors, spacing }) => ({
  marginTop: spacing.md,
  color: colors.textDim,
  fontSize: 12,
})

const $resetButton: ThemedStyle<ViewStyle> = ({ colors, spacing }) => ({
  backgroundColor: colors.primary,
  paddingHorizontal: spacing.xxl,
  paddingVertical: spacing.md,
  borderRadius: 8,
  minWidth: 200,
})

const $resetButtonText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.palette.neutral100,
  fontWeight: "600",
})
